import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { FiArrowUp, FiArrowDown, FiRefreshCw } from 'react-icons/fi';
import api from '../../services/api';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CAT_COLORS = { women:'#FF3F6C', men:'#282C3F', kids:'#7C3AED', accessories:'#FF7C5C', footwear:'#03A685', ethnic:'#F59E0B', home:'#06B6D4', beauty:'#EC4899', studio:'#8B5CF6' };
const STATUS_BADGE = { DELIVERED:'badge-green', SHIPPED:'badge-blue', PROCESSING:'badge-yellow', CANCELLED:'badge-pink', CONFIRMED:'badge-blue', RETURNED:'badge-gray' };

function fmt(val) {
  const n = Number(val) || 0;
  if (n >= 10000000) return `₹${(n/10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n/100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n/1000).toFixed(1)}K`;
  return `₹${n}`;
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/dashboard');
      setStats(res.data);
    } catch (e) {
      console.error('Dashboard fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 16 }}>
        <div style={{ width: 40, height: 40, border: '3px solid #E5E7EB', borderTopColor: '#7C3AED', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ color: 'var(--text-muted)' }}>Loading dashboard data...</p>
      </div>
    );
  }

  const monthly = (stats?.monthlyData || []).map(m => ({
    month: MONTH_NAMES[(m.month || 1) - 1],
    sales: Number(m.revenue) || 0,
    orders: Number(m.orderCount) || 0,
  }));

  const catData = (stats?.categoryBreakdown || []).map(c => {
    const key = (c.category || 'other').toLowerCase();
    return { name: key.charAt(0).toUpperCase() + key.slice(1), value: Number(c.count) || 0, color: CAT_COLORS[key] || '#9CA3AF' };
  });
  const catTotal = catData.reduce((s, c) => s + c.value, 0);

  return (
    <div className="dashboard-page">
      {/* Top-right refresh */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <button className="admin-topbar-btn" onClick={fetchStats} title="Refresh data" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, background: 'var(--bg-elevated)', border: '1px solid var(--border)', fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>
          <FiRefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#FFF0F3' }}>💰</div>
          <div className="stat-info">
            <p className="stat-label">Total Revenue</p>
            <p className="stat-value">{fmt(stats?.totalRevenue)}</p>
            <p className="stat-change up"><FiArrowUp /> All time earnings</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#E8F8F5' }}>🛍️</div>
          <div className="stat-info">
            <p className="stat-label">Total Orders</p>
            <p className="stat-value">{(stats?.totalOrders || 0).toLocaleString()}</p>
            <p className="stat-change up"><FiArrowUp /> {stats?.processingOrders || 0} processing now</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#EFF6FF' }}>👥</div>
          <div className="stat-info">
            <p className="stat-label">Total Customers</p>
            <p className="stat-value">{(stats?.totalUsers || 0).toLocaleString()}</p>
            <p className="stat-change up"><FiArrowUp /> Registered accounts</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#F5F3FF' }}>📦</div>
          <div className="stat-info">
            <p className="stat-label">Products</p>
            <p className="stat-value">{(stats?.totalProducts || 0).toLocaleString()}</p>
            <p className="stat-change down"><FiArrowDown /> {stats?.deliveredOrders || 0} orders delivered</p>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="dashboard-grid">
        <div className="chart-card">
          <h3>Revenue & Orders — Last 6 Months</h3>
          {monthly.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#696B79' }} />
                <YAxis tick={{ fontSize: 12, fill: '#696B79' }} tickFormatter={v => v >= 1000 ? `₹${v/1000}k` : v} />
                <Tooltip formatter={(val, name) => name === 'sales' ? [`₹${val.toLocaleString()}`, 'Revenue'] : [val, 'Orders']} />
                <Line type="monotone" dataKey="sales" stroke="#FF3F6C" strokeWidth={2.5} dot={{ fill: '#FF3F6C', r: 4 }} />
                <Line type="monotone" dataKey="orders" stroke="#7C3AED" strokeWidth={2} strokeDasharray="5 5" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
              No order data yet — place some orders to see the chart
            </div>
          )}
        </div>

        <div className="chart-card">
          <h3>Products by Category</h3>
          {catData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={catData} cx="50%" cy="50%" outerRadius={75} dataKey="value" label={false}>
                    {catData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip formatter={(val, name, p) => [`${catTotal ? Math.round(val/catTotal*100) : 0}% (${val} items)`, p.payload.name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pie-legend">
                {catData.map(c => (
                  <div key={c.name} className="pie-legend-item">
                    <span style={{ background: c.color }} className="pie-dot" />
                    <span>{c.name}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{c.value} items</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
              No products added yet
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="dashboard-grid">
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Recent Orders</h3>
            <a href="/admin/orders" style={{ fontSize: 13, color: 'var(--huge-purple)', fontWeight: 700 }}>View All →</a>
          </div>
          <div style={{ padding: '8px 24px 16px' }}>
            {!(stats?.recentOrders?.length) ? (
              <p style={{ color: 'var(--text-muted)', fontSize: 14, padding: '20px 0', textAlign: 'center' }}>No orders placed yet</p>
            ) : (
              stats.recentOrders.map(order => (
                <div key={order.id} className="recent-order-item">
                  <div>
                    <p className="recent-order-id">#{order.orderNumber}</p>
                    <p className="recent-order-meta">
                      {order.customerName} · {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-IN', { day:'numeric', month:'short' }) : ''}
                    </p>
                  </div>
                  <span className={`table-badge ${STATUS_BADGE[order.status] || 'badge-gray'}`}>{order.status}</span>
                  <span className="recent-order-amount">₹{Number(order.totalAmount).toLocaleString()}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-header"><h3>Order Status Breakdown</h3></div>
          <div style={{ padding: '8px 24px 16px' }}>
            {[
              { label: 'Processing', count: stats?.processingOrders || 0, color: '#F59E0B' },
              { label: 'Shipped', count: stats?.shippedOrders || 0, color: '#3B82F6' },
              { label: 'Delivered', count: stats?.deliveredOrders || 0, color: '#10B981' },
              { label: 'Cancelled', count: stats?.cancelledOrders || 0, color: '#EF4444' },
            ].map(s => {
              const pct = stats?.totalOrders ? Math.round(s.count / stats.totalOrders * 100) : 0;
              return (
                <div key={s.label} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>{s.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: s.color }}>{s.count} ({pct}%)</span>
                  </div>
                  <div style={{ height: 6, background: '#F3F4F6', borderRadius: 3 }}>
                    <div style={{ height: 6, background: s.color, borderRadius: 3, width: `${pct}%`, transition: 'width 0.5s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Revenue Bar Chart */}
      {monthly.length > 0 && (
        <div className="chart-card" style={{ marginBottom: 0 }}>
          <h3>Monthly Revenue Overview</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#696B79' }} />
              <YAxis tick={{ fontSize: 12, fill: '#696B79' }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={val => [`₹${val.toLocaleString()}`, 'Revenue']} />
              <Bar dataKey="sales" fill="#7C3AED" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
